'use client';
import { useEffect, useRef, useState } from 'react';

import { SignupState } from '@/types/Signup';

const STORAGE_KEY = 'vivrelle_signup_application';

/**
 * The subset of `SignupState` persisted to `sessionStorage` so a reload does not
 * wipe an in-progress application. Hard excludes — never persisted:
 *  - `password` (secret),
 *  - all card fields (`creditCardLast4`, `expirationDate`,
 *    `stripePaymentMethodTokenId`, `stripePaymentMethodBrand`) — Stripe elements
 *    cannot be rehydrated anyway,
 *  - the consent checkboxes (`paymentAgreement`, `terms`) — consent must be
 *    given fresh each session.
 */
const PERSIST_KEYS: (keyof SignupState)[] = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'birthday',
  'instagram',
  'tiktok',
  'referralCode',
  'optinPromotion',
  'optinText',
  'sameAsShippingAddress',
  'address',
  'aptUnitSuite',
  'city',
  'state',
  'postalCode',
  'billingAddress',
  'billingAptUnitSuite',
  'billingCity',
  'billingState',
  'billingPostalCode',
  'subscription_level',
  'insurance_level',
  'addon_subscription',
  'plan',
  'utm_source'
];

function pickPersistable(state: SignupState): Partial<SignupState> {
  const out = {} as Record<keyof SignupState, unknown>;
  PERSIST_KEYS.forEach((key) => {
    out[key] = state[key];
  });
  return out as Partial<SignupState>;
}

/**
 * Reads the saved subset, applying the whitelist defensively on read too.
 *
 * Exported because the form needs it *synchronously*, while computing its
 * initial values: the rehydrate effect below lands after the first render, so a
 * form seeded only from context would render empty inputs next to restored
 * context state.
 */
export function readPersistedSignupState(): Partial<SignupState> | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const picked = {} as Record<keyof SignupState, unknown>;
    PERSIST_KEYS.forEach((key) => {
      if (key in parsed) {
        picked[key] = parsed[key];
      }
    });
    return picked as Partial<SignupState>;
  } catch {
    return null;
  }
}

function persist(state: SignupState) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(pickPersistable(state))
    );
  } catch {
    // Storage full / disabled — persistence is best-effort.
  }
}

/**
 * Drops the saved draft once an application has actually been submitted.
 *
 * Without this, a successful submission left the draft sitting in
 * `sessionStorage` — `resetForm()` clears `SignupContext`, but that happens
 * after `router.push` has already navigated away from `/apply`, so this
 * hook's own persist effect never runs to write the cleared state back. The
 * next visit to `/apply` in the same tab (a second application, or the same
 * one after logging out) would otherwise still be pre-filled from the one
 * that already went through.
 */
export function clearPersistedSignupState() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage disabled — nothing to clear.
  }
}

/**
 * Persists a whitelisted subset of `SignupState` to `sessionStorage` and
 * rehydrates it on mount, replacing the `beforeunload` guard Chrome half-ignores.
 *
 * Returns `wasRehydrated` so callers can suppress side effects that belong to a
 * genuinely new session — notably the Braze abandon fire.
 */
export function useSignupPersistence(
  state: SignupState,
  updateFormValues: (values: Partial<SignupState>) => void
): { wasRehydrated: boolean } {
  const [wasRehydrated, setWasRehydrated] = useState(false);

  // Rehydrate once on mount.
  useEffect(() => {
    const saved = readPersistedSignupState();
    if (saved && Object.keys(saved).length > 0) {
      updateFormValues(saved);
      setWasRehydrated(true);
    }
    // updateFormValues is stable (context action); run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist on change. Skip the first run so the initial (empty) state cannot
  // clobber saved data before rehydration is applied.
  const skipFirstPersist = useRef(true);
  useEffect(() => {
    if (skipFirstPersist.current) {
      skipFirstPersist.current = false;
      return;
    }
    persist(state);
  }, [state]);

  return { wasRehydrated };
}

export default useSignupPersistence;
